/** Display helpers: weights in the ranch's units, gains, checks and the plain words for groups and reasons. */
import { convertUnit, type Quantity } from "../../../contract";
import { formatDate, formatNumber, formatQuantity } from "../../../web/lib/format";
import { t } from "../../../web/lib/i18n";
import { useMe } from "../../../web/lib/session";
import { MISSING_TEXT, type WeightCheck } from "../shared/calc";
import { WEIGHT_CONTEXTS, type Gain, type MissingReason, type WeightContext } from "../shared/types";

export type System = "imperial" | "metric";

export function useSystem(): System {
  return useMe().data?.ranch?.units ?? "imperial";
}

export const weightUnit = (system: System) => (system === "metric" ? "kg" : "lb");

export function fmtWeight(q: Quantity | null | undefined, system: System): string {
  return q ? formatQuantity(q, system) : "";
}

export function fmtLb(lb: number | null | undefined, system: System): string {
  return lb === null || lb === undefined ? "" : formatQuantity({ value: lb, unit: "lb" }, system);
}

/** A weight in the ranch's unit as a plain number, for charts and inputs. */
export function inUnit(q: Quantity, system: System): number {
  const unit = weightUnit(system);
  return convertUnit(q.value, q.unit, unit) ?? q.value;
}

export function fmtGain(g: Pick<Gain, "value" | "unit"> | null | undefined, system: System): string {
  if (!g) return "";
  const lbPerDay = g.unit === "kg_per_day" ? convertUnit(g.value, "kg", "lb")! : g.value;
  return fmtGainLb(lbPerDay, system);
}

export function fmtGainLb(lbPerDay: number | null | undefined, system: System): string {
  if (lbPerDay === null || lbPerDay === undefined) return "";
  return system === "metric"
    ? t("{{value}} kg/day", { value: formatNumber(convertUnit(lbPerDay, "lb", "kg")!, 2) })
    : t("{{value}} lb/day", { value: formatNumber(lbPerDay, 2) });
}

export const contextLabel = (c: WeightContext | undefined | null) => (c ? t(WEIGHT_CONTEXTS[c]) : t("Weight"));

export const sexClassLabel = (s: string) => (s === "bull" ? t("Bulls") : s === "steer" ? t("Steers") : t("Heifers"));

export function managementLabel(m: { kind: "code" | "group" | "day"; label: string }): string {
  if (m.kind === "code") return t("Group code {{code}}", { code: m.label });
  if (m.kind === "day") return t("Weighed together {{date}}", { date: formatDate(m.label) });
  return m.label;
}

export const missingText = (reasons: Array<MissingReason | string> | undefined) => (reasons ?? []).map((r) => t(MISSING_TEXT[r as MissingReason] ?? r));

export function pct(n: number): string {
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${formatNumber(Math.abs(n), 0)}%`;
}

/** One sentence that says why a weight wants a look. */
export function checkText(check: WeightCheck, system: System): string {
  const values = { pct: formatNumber(Math.abs(check.pct), 0), weight: fmtWeight(check.previous, system), date: formatDate(check.previous.on) };
  if (check.kind === "same_day") return t("{{pct}}% off the {{weight}} reading on {{date}}. Check the tag and the scale.", values);
  if (check.kind === "drop") return t("Down {{pct}}% from {{weight}} on {{date}}. Check the tag and the reading.", values);
  return t("Up {{pct}}% from {{weight}} on {{date}}, more gain than an animal can put on. Check the tag and the reading.", values);
}

export function ordinal(rank: number, count: number): string {
  return t("{{rank}} of {{count}}", { rank, count });
}
